/**
 * Base Platform Adapter
 *
 * Abstract base class for all platform adapters.
 * Provides common functionality and enforces adapter interface compliance.
 */

import {
  PlatformAdapter,
  TokenSet,
  ExportOptions,
  ValidationResult,
  TokenType,
  TokenExportResult,
} from "../../types/designTokens";

/**
 * Base adapter configuration
 */
export interface BaseAdapterConfig {
  name: string;
  version: string;
  supportedTypes: TokenType[];
  defaultOptions?: ExportOptions;
}

/**
 * Abstract base class for platform adapters
 */
export abstract class BasePlatformAdapter implements PlatformAdapter {
  protected config: BaseAdapterConfig;

  constructor(config: BaseAdapterConfig) {
    this.config = config;
  }

  /**
   * Get adapter name
   */
  get name(): string {
    return this.config.name;
  }

  /**
   * Get adapter version
   */
  get version(): string {
    return this.config.version;
  }

  /**
   * Get supported token types
   */
  get supportedTypes(): TokenType[] {
    return this.config.supportedTypes;
  }

  /**
   * Validate adapter configuration
   */
  protected validateConfig(): void {
    if (!this.config.name) {
      throw new Error("Adapter name is required");
    }
    if (!this.config.version) {
      throw new Error("Adapter version is required");
    }
    if (!Array.isArray(this.config.supportedTypes)) {
      throw new Error("Supported types must be an array");
    }
  }

  /**
   * Generate tokens - must be implemented by subclasses
   */
  abstract generate(tokens: TokenSet, options?: ExportOptions): Promise<string>;

  /**
   * Validate tokens - default implementation
   */
  validate(tokens: TokenSet): ValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Basic validation
    if (!tokens) {
      errors.push("Token set is null or undefined");
      return {
        valid: false,
        errors: errors.map((message, index) => ({
          path: `validation.${index}`,
          message,
          code: "VALIDATION_ERROR",
          severity: "error" as const,
        })),
        warnings: warnings.map((message, index) => ({
          path: `validation.${index}`,
          message,
          code: "VALIDATION_WARNING",
          severity: "warning" as const,
        })),
      };
    }

    // Validate token structure
    this.validateTokenStructure(tokens, "", errors, warnings);

    return {
      valid: errors.length === 0,
      errors: errors.map((message, index) => ({
        path: `validation.${index}`,
        message,
        code: "VALIDATION_ERROR",
        severity: "error" as const,
      })),
      warnings: warnings.map((message, index) => ({
        path: `validation.${index}`,
        message,
        code: "VALIDATION_WARNING",
        severity: "warning" as const,
      })),
    };
  }

  /**
   * Export tokens with result metadata
   */
  async export(
    tokens: TokenSet,
    options: ExportOptions = {},
  ): Promise<TokenExportResult> {
    const content = await this.generate(tokens, options);
    const filename = this.generateFilename(tokens, options);

    return {
      platform: this.name,
      format: options.format || "default",
      content,
      filename,
      size: new Blob([content]).size,
      timestamp: new Date().toISOString(),
      tokens: this.countTokens(tokens),
      warnings: this.getValidationWarnings(tokens),
    };
  }

  /**
   * Validate token structure recursively
   */
  private validateTokenStructure(
    tokens: any,
    path: string,
    errors: string[],
    warnings: string[],
  ): void {
    if (typeof tokens !== "object" || tokens === null) {
      errors.push(`Invalid token structure at ${path}: not an object`);
      return;
    }

    for (const [key, value] of Object.entries(tokens)) {
      const currentPath = path ? `${path}.${key}` : key;

      if (value && typeof value === "object") {
        // Check if it's a token or group
        const objValue = value as any;
        if (objValue.$type) {
          // It's a token
          this.validateToken(objValue, currentPath, errors, warnings);
        } else {
          // It's a group
          this.validateTokenStructure(
            objValue.children || {},
            currentPath,
            errors,
            warnings,
          );
        }
      } else {
        warnings.push(`Invalid value at ${currentPath}: not an object`);
      }
    }
  }

  /**
   * Validate individual token
   */
  private validateToken(
    token: any,
    path: string,
    errors: string[],
    warnings: string[],
  ): void {
    if (!token.$type) {
      errors.push(`Token at ${path} missing $type property`);
      return;
    }

    if (!token.$value) {
      errors.push(`Token at ${path} missing $value property`);
      return;
    }

    // Check if type is supported
    if (!this.supportedTypes.includes(token.$type)) {
      warnings.push(
        `Token type "${token.$type}" at ${path} may not be fully supported`,
      );
    }

    // Type-specific validation
    this.validateTokenValue(token, path, errors, warnings);
  }

  /**
   * Validate token value based on type
   */
  protected validateTokenValue(
    token: any,
    path: string,
    errors: string[],
    warnings: string[],
  ): void {
    const { $type, $value } = token;

    switch ($type) {
      case "color":
        this.validateColorValue($value, path, errors);
        break;

      case "dimension":
      case "fontSize":
      case "lineHeight":
      case "letterSpacing":
        this.validateDimensionValue($value, path, errors);
        break;

      case "font":
        this.validateFontValue($value, path, errors);
        break;

      case "duration":
        this.validateDurationValue($value, path, errors);
        break;

      case "cubicBezier":
        this.validateCubicBezierValue($value, path, errors);
        break;

      case "number":
        if (typeof $value !== "number") {
          errors.push(`Number token at ${path} must have a numeric value`);
        }
        break;

      case "string":
      case "fontFamily":
      case "fontWeight":
      case "strokeStyle":
        if (typeof $value !== "string") {
          errors.push(`String token at ${path} must have a string value`);
        }
        break;

      case "boolean":
        if (typeof $value !== "boolean") {
          errors.push(`Boolean token at ${path} must have a boolean value`);
        }
        break;

      case "link":
        if (typeof $value !== "string" || !$value.startsWith("{")) {
          errors.push(
            `Link token at ${path} must reference another token using {token.path} syntax`,
          );
        }
        break;

      default:
        warnings.push(`Unknown token type "${$type}" at ${path}`);
    }
  }

  /**
   * Validate color value
   */
  protected validateColorValue(
    value: any,
    path: string,
    errors: string[],
  ): void {
    if (!value || typeof value !== "object") {
      errors.push(`Color value at ${path} must be an object`);
      return;
    }

    if (!value.colorSpace) {
      errors.push(`Color value at ${path} missing colorSpace`);
    }

    if (!Array.isArray(value.channels)) {
      errors.push(`Color value at ${path} channels must be an array`);
      return;
    }

    if (value.channels.length < 3 || value.channels.length > 4) {
      errors.push(`Color value at ${path} must have 3 or 4 channels`);
      return;
    }

    for (let i = 0; i < value.channels.length; i++) {
      if (
        typeof value.channels[i] !== "number" ||
        value.channels[i] < 0 ||
        value.channels[i] > 1
      ) {
        errors.push(
          `Color channel ${i} at ${path} must be a number between 0 and 1`,
        );
      }
    }

    if (
      value.alpha !== undefined &&
      (typeof value.alpha !== "number" || value.alpha < 0 || value.alpha > 1)
    ) {
      errors.push(`Color alpha at ${path} must be a number between 0 and 1`);
    }
  }

  /**
   * Validate dimension value
   */
  protected validateDimensionValue(
    value: any,
    path: string,
    errors: string[],
  ): void {
    if (!value || typeof value !== "object") {
      errors.push(`Dimension value at ${path} must be an object`);
      return;
    }

    if (typeof value.value !== "number") {
      errors.push(`Dimension value at ${path} must have a numeric value`);
    }

    if (!value.unit || typeof value.unit !== "string") {
      errors.push(`Dimension value at ${path} must have a string unit`);
    }

    const validUnits = [
      "px",
      "rem",
      "em",
      "vh",
      "vw",
      "vmin",
      "vmax",
      "%",
      "pt",
      "pc",
      "in",
      "cm",
      "mm",
      "deg",
      "rad",
      "grad",
      "turn",
    ];

    if (!validUnits.includes(value.unit)) {
      errors.push(`Invalid dimension unit "${value.unit}" at ${path}`);
    }
  }

  /**
   * Validate font value
   */
  protected validateFontValue(
    value: any,
    path: string,
    errors: string[],
  ): void {
    if (!value || typeof value !== "object") {
      errors.push(`Font value at ${path} must be an object`);
      return;
    }

    if (!Array.isArray(value.fontFamily) || value.fontFamily.length === 0) {
      errors.push(
        `Font value at ${path} must have a non-empty fontFamily array`,
      );
    }

    for (const font of value.fontFamily) {
      if (typeof font !== "string") {
        errors.push(`Font family name at ${path} must be a string`);
      }
    }

    if (value.fontSize) {
      this.validateDimensionValue(value.fontSize, `${path}.fontSize`, errors);
    }

    if (
      value.fontWeight &&
      typeof value.fontWeight !== "number" &&
      typeof value.fontWeight !== "string"
    ) {
      errors.push(`Font weight at ${path} must be a number or string`);
    }

    if (
      value.lineHeight &&
      typeof value.lineHeight !== "string" &&
      typeof value.lineHeight !== "object"
    ) {
      errors.push(
        `Line height at ${path} must be a string or dimension object`,
      );
    }

    if (value.letterSpacing) {
      this.validateDimensionValue(
        value.letterSpacing,
        `${path}.letterSpacing`,
        errors,
      );
    }
  }

  /**
   * Validate duration value
   */
  protected validateDurationValue(
    value: any,
    path: string,
    errors: string[],
  ): void {
    if (!value || typeof value !== "object") {
      errors.push(`Duration value at ${path} must be an object`);
      return;
    }

    if (typeof value.value !== "number") {
      errors.push(`Duration value at ${path} must have a numeric value`);
    }

    if (!value.unit || typeof value.unit !== "string") {
      errors.push(`Duration value at ${path} must have a string unit`);
    }

    const validUnits = ["ms", "s"];
    if (!validUnits.includes(value.unit)) {
      errors.push(`Invalid duration unit "${value.unit}" at ${path}`);
    }

    if (value.value < 0) {
      errors.push(`Duration value at ${path} must be non-negative`);
    }
  }

  /**
   * Validate cubic bezier value
   */
  protected validateCubicBezierValue(
    value: any,
    path: string,
    errors: string[],
  ): void {
    if (!value || typeof value !== "object") {
      errors.push(`Cubic bezier value at ${path} must be an object`);
      return;
    }

    const coords = ["x1", "y1", "x2", "y2"];
    for (const coord of coords) {
      if (
        typeof value[coord] !== "number" ||
        value[coord] < 0 ||
        value[coord] > 1
      ) {
        errors.push(
          `Cubic bezier ${coord} at ${path} must be a number between 0 and 1`,
        );
      }
    }
  }

  /**
   * Generate filename for export
   */
  protected generateFilename(tokens: TokenSet, options: ExportOptions): string {
    const baseName = tokens.$metadata?.name || "tokens";
    const formattedName = baseName.toLowerCase().replace(/\s+/g, "-");
    const extension = this.getFileExtension(options);
    return `${formattedName}.${extension}`;
  }

  /**
   * Get file extension for export format
   */
  protected getFileExtension(options: ExportOptions): string {
    switch (options.format) {
      case "json":
        return "json";
      case "css":
        return "css";
      case "scss":
        return "scss";
      case "sass":
        return "sass";
      case "less":
        return "less";
      case "js":
        return "js";
      case "ts":
        return "ts";
      default:
        return "txt";
    }
  }

  /**
   * Count total tokens in set
   */
  protected countTokens(tokens: TokenSet): number {
    let count = 0;

    const countInGroup = (group: any): number => {
      if (!group || typeof group !== "object") {
        return 0;
      }

      if (group.$type && group.$type !== "group") {
        return 1; // It's a token
      }

      if (group.children) {
        return Object.values(group.children).reduce<number>(
          (sum: number, child) => sum + countInGroup(child),
          0,
        );
      }

      return 0;
    };

    if (tokens.tokens) {
      count += countInGroup(tokens.tokens);
    }

    return count;
  }

  /**
   * Get validation warnings
   */
  protected getValidationWarnings(tokens: TokenSet): string[] {
    const warnings: string[] = [];

    // Check for deprecated tokens
    const checkDeprecated = (group: any, path: string) => {
      if (
        group &&
        typeof group === "object" &&
        group.$deprecated &&
        !group.$deprecatedComment
      ) {
        warnings.push(
          `Deprecated token at ${path} missing deprecation comment`,
        );
      }
    };

    if (tokens.tokens) {
      this.checkDeprecatedInGroup(tokens.tokens, "");
    }

    return warnings;
  }

  /**
   * Check deprecated tokens in group
   */
  private checkDeprecatedInGroup(group: any, path: string): void {
    if (group.children) {
      for (const [key, value] of Object.entries(group.children)) {
        this.checkDeprecatedInGroup(value, `${path}.${key}`);
      }
    } else {
      // Check if the token/group itself is deprecated
      const objGroup = group as any;
      if (objGroup.deprecated) {
        console.warn(`Deprecated token/group at ${path}`);
      }
    }
  }
}
